"use client";

import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import {
	Accordion,
	Button,
	CheckboxGroup,
	CheckboxGroupItem,
	Heading,
	InputNumber,
	Sheets,
	Text,
	type CheckboxGroupProps
} from "@pamperedchef/design-system";

import { className as classNameHelper } from "@/composites/foundation/utilities/className";

import {
	isFilterGroupActive,
	selectedCount,
	type FilterGroup,
	type FilterPanelPresentation,
	type FilterRangeChange,
	type FilterValue,
	type FilterValueChange,
	type ListFilterGroup,
	type RangeFilterGroup,
	type ToggleFilterGroup
} from "../types";

import * as styles from "./FilterPanel.module.scss";

type CheckboxGroupChange = CheckboxGroupProps["onChange"];

export interface FilterPanelLabels {
	title: string;
	description: string;
	trigger: string;
	apply: string;
	close: string;
	showMore: string;
	showLess: string;
	min: string;
	max: string;
	unlabelledGroup: string;
}

const DEFAULT_LABELS: FilterPanelLabels = {
	title: "Filters",
	description: "Filters apply upon selection.",
	trigger: "Filters",
	apply: "Apply",
	close: "Close filters",
	showMore: "Show more",
	showLess: "Show less",
	min: "Min",
	max: "Max",
	unlabelledGroup: "More filters"
};

export interface FilterPanelProps {
	groups: FilterGroup[];
	onValueChange: (change: FilterValueChange) => void;
	onRangeChange?: (change: FilterRangeChange) => void;
	presentation?: FilterPanelPresentation;
	labels?: Partial<FilterPanelLabels>;
	isOpen?: boolean;
	onOpenChange?: (isOpen: boolean) => void;
	drawerSummary?: ReactNode;
	className?: string;
}

export function FilterPanel({
	groups,
	onValueChange,
	onRangeChange = noop,
	presentation = "inline",
	labels,
	isOpen,
	onOpenChange,
	drawerSummary,
	className
}: FilterPanelProps): React.JSX.Element | null {
	const text = { ...DEFAULT_LABELS, ...labels };
	const headingId = useId();
	const sidebarRef = useRef<HTMLElement>(null);
	const [isOpenFallback, setIsOpenFallback] = useState(false);

	const visibleGroups = groups.filter(isFilterGroupActive);
	const resolvedIsOpen = isOpen ?? isOpenFallback;
	const setOpen = (open: boolean): void => {
		if (isOpen === undefined) setIsOpenFallback(open);
		onOpenChange?.(open);
	};

	const isInline = presentation === "inline";
	const mustClose =
		resolvedIsOpen && (isInline || visibleGroups.length === 0);
	useEffect(() => {
		if (!mustClose) return;
		if (isOpen === undefined) setIsOpenFallback(false);
		onOpenChange?.(false);
		if (!isInline) return;
		requestAnimationFrame(() =>
			requestAnimationFrame(() => {
				const sidebar = sidebarRef.current;
				const active = sidebar?.ownerDocument.activeElement;
				if (!active || active === sidebar?.ownerDocument.body) {
					sidebar?.focus();
				}
			})
		);
	}, [mustClose, isInline, isOpen, onOpenChange]);

	if (visibleGroups.length === 0) return null;

	if (isInline) {
		return (
			<section
				ref={sidebarRef}
				tabIndex={-1}
				aria-labelledby={headingId}
				{...classNameHelper(styles.panel, className)}
				data-component="FilterPanel"
				data-presentation="inline"
			>
				<Heading tag="h2" id={headingId} className={styles.title}>
					{text.title}
				</Heading>
				<FilterGroups
					groups={visibleGroups}
					onValueChange={onValueChange}
					onRangeChange={onRangeChange}
					labels={text}
				/>
			</section>
		);
	}

	return (
		<Sheets
			isOpen={resolvedIsOpen}
			onOpenChange={setOpen}
			title={text.title}
			closeButtonLabel={text.close}
			mobileVariant="fullscreen"
			triggerElement={
				<Button
					kind="tertiary"
					size="sm"
					leadingIcon={{ name: "filter" }}
					className={className}
				>
					{text.trigger}
				</Button>
			}
			primaryButton={
				<Button onPress={() => setOpen(false)}>{text.apply}</Button>
			}
		>
			<div
				className={styles.drawer}
				data-component="FilterPanel"
				data-presentation="drawer"
			>
				<Text tag="p" className={styles.description}>
					{text.description}
				</Text>
				{drawerSummary}
				<FilterGroups
					groups={visibleGroups}
					onValueChange={onValueChange}
					onRangeChange={onRangeChange}
					labels={text}
				/>
			</div>
		</Sheets>
	);
}

function noop(): void {}

interface FilterSection {
	id: string;
	label: string;
	groups: FilterGroup[];
}

interface GroupHandlers {
	onValueChange: (change: FilterValueChange) => void;
	onRangeChange: (change: FilterRangeChange) => void;
	labels: FilterPanelLabels;
}

interface FilterGroupsProps extends GroupHandlers {
	groups: FilterGroup[];
}

function toSections(groups: FilterGroup[]): {
	leading: FilterGroup[];
	sections: FilterSection[];
} {
	const leading: FilterGroup[] = [];
	const sections: FilterSection[] = [];
	for (const group of groups) {
		const last = sections.at(-1);
		if (group.label) {
			sections.push({
				id: group.id,
				label: group.label,
				groups: [group]
			});
		} else if (last) {
			last.groups.push(group);
		} else {
			leading.push(group);
		}
	}
	return { leading, sections };
}

function FilterGroups({
	groups,
	...handlers
}: FilterGroupsProps): React.JSX.Element {
	const { leading, sections } = toSections(groups);
	const openWithSelection = sections.flatMap((section, index) =>
		section.groups.some((group) => selectedCount(group) > 0) ? [index] : []
	);

	return (
		<div className={styles.groups}>
			{leading.length > 0 && (
				<div className={styles.leading}>
					{leading.map((group) => (
						<FilterGroupBody
							key={group.id}
							group={group}
							{...handlers}
						/>
					))}
				</div>
			)}
			{sections.length > 0 && (
				<Accordion
					key={sections.map((section) => section.id).join("|")}
					initialOpen={openWithSelection}
					sections={sections.map((section) => ({
						title: sectionTitle(section),
						content: (
							<div key={section.id} className={styles.section}>
								{section.groups.map((group) => (
									<FilterGroupBody
										key={group.id}
										group={group}
										{...handlers}
									/>
								))}
							</div>
						)
					}))}
				/>
			)}
		</div>
	);
}

interface FilterGroupBodyProps extends GroupHandlers {
	group: FilterGroup;
}

function FilterGroupBody({
	group,
	...handlers
}: FilterGroupBodyProps): React.JSX.Element {
	switch (group.kind) {
		case "list":
			return <FilterGroupValues group={group} {...handlers} />;
		case "toggle":
			return <FilterToggle group={group} {...handlers} />;
		case "range":
			return <FilterRangeInputs group={group} {...handlers} />;
	}
}

function FilterToggle({
	group,
	onValueChange
}: GroupHandlers & { group: ToggleFilterGroup }): React.JSX.Element {
	const { value } = group;
	const label = valueLabel(value);

	return (
		<div className={styles.values}>
			<CheckboxGroup
				aria-label={group.label ?? value.label}
				value={value.selected ? [value.value] : []}
				onChange={
					((next: string[]) =>
						onValueChange({
							groupId: group.id,
							value: value.value,
							selected: next.includes(value.value)
						})) as CheckboxGroupChange
				}
			>
				<CheckboxGroupItem value={value.value} aria-label={label}>
					{label}
				</CheckboxGroupItem>
			</CheckboxGroup>
		</div>
	);
}

function FilterRangeInputs({
	group,
	onRangeChange,
	labels
}: GroupHandlers & { group: RangeFilterGroup }): React.JSX.Element {
	const { bounds, selected } = group;

	function change(next: { min?: number; max?: number }): void {
		onRangeChange({ groupId: group.id, ...selected, ...next });
	}

	return (
		<div className={styles.range}>
			<InputNumber
				label={labels.min}
				value={selected.min ?? Number.NaN}
				minValue={bounds.min}
				maxValue={selected.max ?? bounds.max}
				formatOptions={group.formatOptions}
				onChange={(min) => change({ min: toBound(min) })}
			/>
			<InputNumber
				label={labels.max}
				value={selected.max ?? Number.NaN}
				minValue={selected.min ?? bounds.min}
				maxValue={bounds.max}
				formatOptions={group.formatOptions}
				onChange={(max) => change({ max: toBound(max) })}
			/>
		</div>
	);
}

function toBound(value: number): number | undefined {
	return Number.isFinite(value) ? value : undefined;
}

interface FilterGroupValuesProps extends GroupHandlers {
	group: ListFilterGroup;
}

function FilterGroupValues({
	group,
	onValueChange,
	labels
}: FilterGroupValuesProps): React.JSX.Element {
	const cutoff = overflowCutoff(group);
	const hiddenSelection =
		cutoff === undefined
			? ""
			: group.values
					.slice(cutoff)
					.filter((value) => value.selected)
					.map((value) => value.value)
					.join("|");
	const [isExpanded, setIsExpanded] = useState(hiddenSelection !== "");
	const [seenHiddenSelection, setSeenHiddenSelection] =
		useState(hiddenSelection);
	if (hiddenSelection !== seenHiddenSelection) {
		setSeenHiddenSelection(hiddenSelection);
		if (hiddenSelection !== "") setIsExpanded(true);
	}
	const shown =
		cutoff === undefined || isExpanded
			? group.values
			: group.values.slice(0, cutoff);
	const selected = group.values
		.filter((value) => value.selected)
		.map((value) => value.value);

	function handleChange(next: string[]): void {
		const added = next.find((value) => !selected.includes(value));
		if (added !== undefined) {
			onValueChange({ groupId: group.id, value: added, selected: true });
			return;
		}
		const removed = selected.find((value) => !next.includes(value));
		if (removed !== undefined) {
			onValueChange({
				groupId: group.id,
				value: removed,
				selected: false
			});
		}
	}

	return (
		<div className={styles.values}>
			<CheckboxGroup
				aria-label={group.label ?? labels.unlabelledGroup}
				value={selected}
				onChange={handleChange as CheckboxGroupChange}
			>
				{shown.map((value) => {
					const label = valueLabel(value);
					return (
						<CheckboxGroupItem
							key={value.value}
							value={value.value}
							aria-label={label}
						>
							{label}
						</CheckboxGroupItem>
					);
				})}
			</CheckboxGroup>
			{cutoff !== undefined && (
				<Button
					kind="tertiary"
					size="sm"
					aria-expanded={isExpanded}
					onPress={() => setIsExpanded((expanded) => !expanded)}
				>
					{isExpanded ? labels.showLess : labels.showMore}
				</Button>
			)}
		</div>
	);
}

function overflowCutoff(group: ListFilterGroup): number | undefined {
	const { maxShown } = group;
	if (!maxShown || maxShown < 1 || group.values.length <= maxShown) {
		return undefined;
	}
	return maxShown;
}

function sectionTitle(section: FilterSection): string {
	const count = section.groups.reduce(
		(total, group) => total + selectedCount(group),
		0
	);
	return count > 0 ? `${section.label} (${count})` : section.label;
}

function valueLabel(value: FilterValue): string {
	return value.count === undefined
		? value.label
		: `${value.label} (${value.count})`;
}
